import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Test, TestQuestion, TestSession, Subject, QuestionStatus, QuestionOption } from '../types/test';
import testService from '../services/testService';
import behaviorAnalyticsService from '../services/behaviorAnalyticsService';
import examLockdownService, { LockdownState } from '../services/examLockdownService';
import { renderLatex } from '../utils/latex';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import VirtualKeyboard from '@/components/VirtualKeyboard';

const EnhancedTestTakingPage: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();

  // Helper function to extract question text and remove subject prefixes
  const getQuestionText = (question: TestQuestion): string => {
    let text = question.questionText || question.question || '';
    if (!text) {
      console.warn('Question missing text:', question);
      return '';
    }

    // Remove subject prefixes like "Mathematics Question 5:", "Physics Question 2:", etc.
    // Enhanced regex to catch more variations
    text = text.replace(/^[A-Za-z\s]+\s+Question\s+\d+:\s*/, '');
    text = text.replace(/^[A-Za-z\s]+\s+Q\d+:\s*/, '');
    text = text.replace(/^Q\d+:\s*/, '');
    text = text.replace(/^\d+\.\s*/, '');

    return text;
  };

  // Helper function to extract option text (unused but kept for compatibility)
  /*
  // Helper function to extract option text (unused but kept for compatibility)
  const getOptionText = (option: QuestionOption | string | any): string => {
    // Handle string options - check if it's a JSON string first
    if (typeof option === 'string') {
      // Check if the string looks like JSON (starts with { and ends with })
      if (option.trim().startsWith('{') && option.trim().endsWith('}')) {
        try {
          // Parse the JSON string to get the actual option object
          const parsedOption = JSON.parse(option);

          // Extract text from the parsed object
          const text = parsedOption.text ||
                       parsedOption.optionText ||
                       parsedOption.value ||
                       parsedOption.content ||
                       parsedOption.label;

          if (text && typeof text === 'string') {
            return text;
          }

          return `[Option text not found in parsed JSON]`;
        } catch (error) {
          // If JSON parsing fails, return the string as-is
          return option;
        }
      } else {
        // Regular string option
        return option;
      }
    }

    // Handle object options (from super-admin service or other formats)
    if (typeof option === 'object' && option !== null) {
      // Try various property names that might contain the option text
      const text = option.text ||
                   option.optionText ||
                   option.value ||
                   option.content ||
                   option.label;

      if (text && typeof text === 'string') {
        return text;
      }

      // Return a fallback message instead of JSON.stringify
      return `[Option text not found]`;
    }

    // Handle null/undefined
    return String(option || '');
  };
  */



  // Helper function to get options as objects with text and imageUrl
  const getOptionsAsObjects = (options: QuestionOption[] | string[] | undefined): { text: string; imageUrl?: string }[] => {
    if (!options) return [];
    return options.map(option => {
      if (typeof option === 'string') {
        // Check if the string looks like JSON
        if (option.trim().startsWith('{') && option.trim().endsWith('}')) {
          try {
            const parsedOption = JSON.parse(option);
            return {
              text: parsedOption.text || parsedOption.optionText || option,
              imageUrl: parsedOption.imageUrl
            };
          } catch (e) {
            return { text: option };
          }
        }
        return { text: option };
      } else if (option && typeof option === 'object') {
        return {
          text: option.text || '',
          imageUrl: option.imageUrl
        };
      }
      return { text: String(option || '') };
    });
  };

  const [test, setTest] = useState<Test | null>(null);
  const [questions, setQuestions] = useState<TestQuestion[]>([]);
  const [session, setSession] = useState<TestSession | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, any>>({});
  const [markedForReview, setMarkedForReview] = useState<Set<string>>(new Set());
  const [visitedQuestions, setVisitedQuestions] = useState<Set<number>>(new Set());
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [activeSubject, setActiveSubject] = useState<string>('');
  const [numericAnswer, setNumericAnswer] = useState<string>('');

  const [showTimeUpModal, setShowTimeUpModal] = useState(false);
  const [showUnansweredAlert, setShowUnansweredAlert] = useState(false);
  const [unansweredQuestions, setUnansweredQuestions] = useState<number[]>([]);
  const [showExitDialog, setShowExitDialog] = useState(false);
  // When true, test is expired: lock inputs and block submission
  const [isTimeExpired, setIsTimeExpired] = useState<boolean>(false);
  const [expiredMessage, setExpiredMessage] = useState<string>('Your test time has expired. You can still submit your test, but you cannot change your answers.');

  // Exam lockdown state
  const [lockdownState, setLockdownState] = useState<LockdownState>(examLockdownService.getState());
  const lockdownAutoSubmitTriggered = useRef(false);

  // Time tracking for analytics
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());
  const [questionTimeSpent, setQuestionTimeSpent] = useState<Record<string, number>>({});
  const [totalTimeSpent, setTotalTimeSpent] = useState<number>(0);

  const disableLockdownForSubmission = useCallback(() => {
    examLockdownService.deactivate();
    setLockdownState(examLockdownService.getState());
  }, []);

  const restoreLockdownAfterFailedSubmission = useCallback(async () => {
    lockdownAutoSubmitTriggered.current = false;
    await examLockdownService.activate();
    setLockdownState(examLockdownService.getState());
  }, []);

  // Define handleAutoSubmit first since it's used in useEffect
  const handleAutoSubmit = useCallback(async () => {
    if (isSubmitting) return;

    // Show time up modal first
    setShowTimeUpModal(true);
    disableLockdownForSubmission();

    setIsSubmitting(true);
    try {
      const result = await testService.submitTest();

      // Clear localStorage backup data on successful submission
      localStorage.removeItem(`test-answers-${testId}`);
      localStorage.removeItem(`test-marked-${testId}`);

      // Wait a moment for user to see the modal, then navigate
      setTimeout(() => {
        navigate(`/results/${testId}`, {
          state: { result, autoSubmitted: true }
        });
      }, 2000);
    } catch (error) {
      console.error('Auto-submit failed:', error);
      setIsSubmitting(false);
      setShowTimeUpModal(false);
      await restoreLockdownAfterFailedSubmission();
    }
  }, [disableLockdownForSubmission, isSubmitting, navigate, restoreLockdownAfterFailedSubmission, testId, setShowTimeUpModal]);

  useEffect(() => {
    if (testId) {
      initializeTest();
    }
  }, [testId]);

  // Enter fullscreen on component mount
  useEffect(() => {
    const enterFullscreen = async () => {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (error) {
        // Fullscreen failed, but continue with the test
      }
    };

    enterFullscreen();

    // Exit fullscreen on component unmount
    return () => {
      try {
        if (document.exitFullscreen && document.fullscreenElement) {
          document.exitFullscreen();
        }
      } catch (error) {
        // Ignore exit fullscreen errors
      }
    };
  }, []);

  // Backend-driven timer sync effect - NO LOCAL COUNTDOWN
  // The timer is maintained on the backend based on session start time
  // Frontend only displays the current state from backend
  useEffect(() => {
    if (!testId) return;

    // Sync with backend timer every 5 seconds
    const syncInterval = setInterval(async () => {
      try {
        const syncData = await testService.syncTimer(testId);

        // Update frontend display with backend timer
        setTimeRemaining(syncData.timeRemaining);

        console.log('Timer synced successfully:', syncData);

        if (syncData.isExpired || syncData.timeRemaining <= 0) {
          // ALWAYS auto-submit when timer expires (regardless of test type or configuration)
          // This prevents tests from being stuck in IN_PROGRESS state permanently
          console.log('Test timer expired, auto-submitting...');
          alert(syncData.message || 'Time\'s up! Your test has been automatically submitted.');
          handleAutoSubmit();
        }
      } catch (error) {
        console.error('Timer sync failed:', error);
        // On sync failure, don't change the timer - keep last known state
      }
    }, 5000); // Sync every 5 seconds for better accuracy

    // Initial sync when component mounts
    const initialSync = async () => {
      try {
        const syncData = await testService.syncTimer(testId);
        setTimeRemaining(syncData.timeRemaining);
        console.log('Initial timer sync:', syncData);
      } catch (error) {
        console.error('Failed initial timer sync:', error);
      }
    };

    initialSync();

    return () => clearInterval(syncInterval);
  }, [testId, handleAutoSubmit]);

  // Display-only countdown for smooth UI (updates every second but doesn't control timer)
  // This provides smooth countdown display between backend syncs
  useEffect(() => {
    if (timeRemaining > 0) {
      const displayTimer = setInterval(() => {
        setTimeRemaining((prev: number) => {
          // Only decrement for display, backend sync will correct any drift
          const newTime = Math.max(0, prev - 1);
          if (newTime <= 0) {
            // Lock UI locally; backend sync will handle any submission for scheduled tests
            setIsTimeExpired(true);
            return 0;
          }
          return newTime;
        });
      }, 1000);

      return () => clearInterval(displayTimer);
    }
  }, [timeRemaining > 0]);

  // Navigation warning effect
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (timeRemaining > 0 && !isSubmitting) {
        e.preventDefault();
        e.returnValue = 'Are you sure you want to leave? Your test progress may be lost.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [timeRemaining, isSubmitting]);

  // Focus tracking for analytics
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        behaviorAnalyticsService.trackFocusLoss('TAB_SWITCH');
      } else {
        behaviorAnalyticsService.trackFocusGain();
      }
    };

    const handleBlur = () => {
      behaviorAnalyticsService.trackFocusLoss('WINDOW_BLUR');
    };

    const handleFocus = () => {
      behaviorAnalyticsService.trackFocusGain();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Exam lockdown: activate on mount, deactivate on unmount
  useEffect(() => {
    examLockdownService.activate();
    const unsubscribe = examLockdownService.subscribe((state) => {
      setLockdownState(state);
    });
    return () => {
      unsubscribe();
      examLockdownService.deactivate();
    };
  }, []);

  // Auto-submit when lockdown violation limit is reached
  useEffect(() => {
    if (lockdownState.shouldAutoSubmit && !lockdownAutoSubmitTriggered.current && !isSubmitting) {
      lockdownAutoSubmitTriggered.current = true;
      handleAutoSubmit();
    }
  }, [lockdownState.shouldAutoSubmit, isSubmitting, handleAutoSubmit]);

  // Auto-select subject button when current question changes
  useEffect(() => {
    if (questions.length > 0 && currentQuestionIndex >= 0 && currentQuestionIndex < questions.length) {
      const currentQuestion = questions[currentQuestionIndex];
      if (currentQuestion.subject && currentQuestion.subject !== activeSubject) {
        setActiveSubject(currentQuestion.subject);
      }
    }
  }, [currentQuestionIndex, questions, activeSubject]);

  // Backup answers to localStorage for persistence across refreshes
  useEffect(() => {
    if (testId && selectedAnswers && Object.keys(selectedAnswers).length > 0) {
      localStorage.setItem(`test-answers-${testId}`, JSON.stringify(selectedAnswers));
    }
  }, [selectedAnswers, testId]);

  // Backup marked questions to localStorage
  useEffect(() => {
    if (testId && markedForReview.size > 0) {
      localStorage.setItem(`test-marked-${testId}`, JSON.stringify(Array.from(markedForReview)));
    }
  }, [markedForReview, testId]);

  // Time tracking effect - track time spent on each question
  useEffect(() => {
    if (questions.length > 0 && currentQuestionIndex >= 0) {
      const currentQuestion = questions[currentQuestionIndex];
      if (currentQuestion) {
        setQuestionStartTime(Date.now());
      }
    }
  }, [currentQuestionIndex, questions]);

  // Track time when leaving a question
  useEffect(() => {
    return () => {
      if (questions.length > 0 && currentQuestionIndex >= 0) {
        const currentQuestion = questions[currentQuestionIndex];
        if (currentQuestion && questionStartTime) {
          const timeSpent = Date.now() - questionStartTime;
          setQuestionTimeSpent((prev: Record<string, number>) => ({
            ...prev,
            [currentQuestion.id]: (prev[currentQuestion.id] || 0) + timeSpent
          }));
          setTotalTimeSpent((prev: number) => prev + timeSpent);
        }
      }
    };
  }, [currentQuestionIndex, questions, questionStartTime]);

  const initializeTest = async () => {
    try {
      if (!testId) {
        return;
      }

      const [testData, questionsData] = await Promise.all([
        testService.getTestById(testId),
        testService.getTestQuestions(testId)
      ]);

      if (!testData) {
        navigate('/');
        return;
      }

      setTest(testData);
      setQuestions(questionsData);

      // Group questions by subject
      const subjectMap = new Map<string, number>();
      questionsData.forEach(q => {
        subjectMap.set(q.subject, (subjectMap.get(q.subject) || 0) + 1);
      });

      const subjectList: Subject[] = Array.from(subjectMap.entries()).map(([name, count]) => ({
        id: name.toLowerCase(),
        name,
        color: getSubjectColor(name),
        questionCount: count
      }));

      setSubjects(subjectList);
      if (subjectList.length > 0) {
        setActiveSubject(subjectList[0].name);
      }

      // Initialize session
      let sessionData = testService.getCurrentSession();

      if (!sessionData) {
        // Try to resume existing session first (for when user refreshes or navigates back)
        sessionData = await testService.resumeTest(testId);
        
        // If no existing session, start a new one
        if (!sessionData) {
          sessionData = await testService.startTest(testId);
        }
      }

      setSession(sessionData);

      // Initialize behavior analytics tracking
      behaviorAnalyticsService.startSession();

      // Sync timer with server to ensure accuracy
      try {
        const timerSync = await testService.syncTimer(testId);
        if (timerSync.isExpired || timerSync.timeRemaining <= 0) {
          // Test has already expired
          console.log('Test expired, creating result and redirecting. Timer sync:', timerSync);

          // Handle expiration based on test type and force submission flag
          if (timerSync.forceSubmission) {
            // Scheduled test: Force submission with alert
            console.log('Scheduled test expired on start, force submitting...');
            alert(timerSync.message || 'This scheduled test has already expired and will be submitted automatically.');

            try {
              const { testResultService } = await import('../services/testResultService');
              const expiredResult = await testResultService.createExpiredTestResult(testId);
              navigate(`/results/${expiredResult.id}`, {
                state: { autoSubmitted: true, expired: true }
              });
            } catch (error) {
              console.error('Failed to create expired test result:', error);
              navigate(`/results/${testId}`, {
                state: { autoSubmitted: true, expired: true }
              });
            }
            return;
          } else {
            // On-demand test: lock answers, allow submit
            console.log('On-demand test expired on start, locking answers but allowing submission...');
            setIsTimeExpired(true);
            setExpiredMessage(timerSync.message || 'Your test time has expired. You can still submit your test, but you cannot change your answers.');
            setTimeRemaining(0);
            // Continue rendering screen in review-only mode
          }
        }
        setTimeRemaining(timerSync.timeRemaining);
        console.log('Timer synced successfully:', timerSync);
      } catch (error) {
        console.error('Timer sync failed, using session time:', error);
        // Check if session time indicates expiry
        if (sessionData.timeRemaining <= 0) {
          console.log('Session time expired, creating result and redirecting');
          try {
            const { testResultService } = await import('../services/testResultService');
            const expiredResult = await testResultService.createExpiredTestResult(testId);
            navigate(`/results/${expiredResult.id}`, {
              state: { autoSubmitted: true, expired: true }
            });
          } catch (error) {
            console.error('Failed to create expired test result:', error);
            navigate(`/results/${testId}`, {
              state: { autoSubmitted: true, expired: true }
            });
          }
          return;
        }
        setTimeRemaining(sessionData.timeRemaining);
      }

      // Ensure selectedAnswers is always an object, never null/undefined
      const answers = sessionData.answers || {};

      // Try to restore from localStorage as backup
      const backupAnswers = localStorage.getItem(`test-answers-${testId}`);
      const backupMarked = localStorage.getItem(`test-marked-${testId}`);

      if (backupAnswers) {
        try {
          const parsedBackupAnswers = JSON.parse(backupAnswers);
          // Merge server answers with backup answers (server takes precedence)
          const mergedAnswers = { ...parsedBackupAnswers, ...answers };
          setSelectedAnswers(mergedAnswers);
        } catch (error) {
          console.error('Failed to parse backup answers:', error);
          setSelectedAnswers(answers);
        }
      } else {
        setSelectedAnswers(answers);
      }

      let markedSet = new Set(sessionData.markedForReview || []);
      if (backupMarked) {
        try {
          const parsedBackupMarked = JSON.parse(backupMarked);
          // Merge server marked with backup marked
          parsedBackupMarked.forEach((id: string) => markedSet.add(id));
        } catch (error) {
          console.error('Failed to parse backup marked questions:', error);
        }
      }
      setMarkedForReview(markedSet);
      const initialQuestionIndex = sessionData.currentQuestionIndex || 0;
      setCurrentQuestionIndex(initialQuestionIndex);

      // Mark initial question as visited
      setVisitedQuestions(new Set([initialQuestionIndex]));

      // Set numeric answer if current question is numeric or fill-in-the-blank
      if (questionsData.length > 0 && sessionData.currentQuestionIndex !== undefined) {
        const currentQ = questionsData[sessionData.currentQuestionIndex];
        if (currentQ && (currentQ.questionType === 'NUMERIC_ANSWER' || currentQ.questionType === 'NUMERIC' || currentQ.questionType === 'FILL_IN_THE_BLANK' || currentQ.questionType === 'FILL_IN_THE_BLANK_NUMERIC') && answers[currentQ.id]) {
          setNumericAnswer(String(answers[currentQ.id] || ''));
        }
      }

    } catch (error: any) {
      console.error('Failed to initialize test:', error);

      // Show user-friendly error message
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      alert(`Failed to load test: ${errorMessage}`);
      navigate('/');
    }
  };

  const getSubjectColor = (subject: string): string => {
    const colors: Record<string, string> = {
      'Mathematics': 'orange',
      'Math': 'orange',
      'Physics': 'blue',
      'Chemistry': 'green',
      'Biology': 'blue'
    };
    return colors[subject] || 'gray';
  };

  const getQuestionStatus = (question: TestQuestion, index: number): QuestionStatus => {
    if (index === currentQuestionIndex) return 'current';

    const isAnswered = selectedAnswers && selectedAnswers.hasOwnProperty(question.id);
    const isMarked = markedForReview.has(question.id);
    const isVisited = visitedQuestions.has(index); // Question is visited if user has seen it

    if (isAnswered && isMarked) return 'answered-marked';
    if (isAnswered) return 'answered';
    if (isMarked) return 'marked';
    if (isVisited) return 'unanswered'; // Visited but not answered
    return 'not-visited'; // Never visited
  };



  const handleAnswerSelect = async (questionId: string, answer: any) => {
    if (isTimeExpired) return; // Block changes after expiry

    // Track answer change in analytics
    const previousAnswer = selectedAnswers?.[questionId];
    behaviorAnalyticsService.trackAnswerChange(
      questionId,
      previousAnswer !== undefined ? String(previousAnswer) : null,
      answer !== null ? String(answer) : null
    );

    // Update local state immediately
    setSelectedAnswers((prev: Record<string, any>) => ({
      ...prev,
      [questionId]: answer
    }));

    // Save to backend immediately for persistence
    try {
      await testService.saveAnswer(questionId, answer);
      console.log('Answer saved successfully for question:', questionId);
    } catch (error) {
      console.error('Failed to save answer:', error);
      // Show user-friendly error message
      alert('Failed to save your answer. Please try again.');
    }
  };

  const handleMultipleAnswerSelect = async (questionId: string, optionIndex: number) => {
    if (isTimeExpired) return; // Block changes after expiry
    // Update local state for multiple correct answers
    setSelectedAnswers((prev: Record<string, any>) => {
      const currentAnswers = prev?.[questionId];
      let newAnswers: number[];

      if (Array.isArray(currentAnswers)) {
        // Toggle the option in the array
        if (currentAnswers.includes(optionIndex)) {
          newAnswers = currentAnswers.filter(index => index !== optionIndex);
        } else {
          newAnswers = [...currentAnswers, optionIndex];
        }
      } else {
        // Convert single answer to array or create new array
        newAnswers = currentAnswers === optionIndex ? [] : [optionIndex];
      }

      return {
        ...prev,
        [questionId]: newAnswers
      };
    });

    // Save to backend immediately for persistence
    try {
      const currentAnswers = selectedAnswers?.[questionId];
      let newAnswers: number[];

      if (Array.isArray(currentAnswers)) {
        if (currentAnswers.includes(optionIndex)) {
          newAnswers = currentAnswers.filter(index => index !== optionIndex);
        } else {
          newAnswers = [...currentAnswers, optionIndex];
        }
      } else {
        newAnswers = currentAnswers === optionIndex ? [] : [optionIndex];
      }

      await testService.saveAnswer(questionId, newAnswers);
      console.log('Multiple answer saved successfully for question:', questionId);
    } catch (error) {
      console.error('Failed to save multiple answer:', error);
      alert('Failed to save your answer. Please try again.');
    }
  };

  const handleNumericAnswerChange = (value: string) => {
    if (isTimeExpired) return; // Block changes after expiry
    setNumericAnswer(value);
    if (questions[currentQuestionIndex]) {
      handleAnswerSelect(questions[currentQuestionIndex].id, value);
    }
  };

  const handleClearResponse = async () => {
    if (isTimeExpired) return; // Block changes after expiry
    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) return;

    // Clear the answer from local state
    setSelectedAnswers((prev: Record<string, any>) => {
      const updated = { ...prev };
      delete updated[currentQuestion.id];
      return updated;
    });

    // Clear numeric answer if it's a numeric or fill-in-the-blank question
    if (currentQuestion.questionType === 'NUMERIC_ANSWER' || currentQuestion.questionType === 'NUMERIC' || currentQuestion.questionType === 'FILL_IN_THE_BLANK' || currentQuestion.questionType === 'FILL_IN_THE_BLANK_NUMERIC') {
      setNumericAnswer('');
    }

    // Clear from backend
    try {
      await testService.saveAnswer(currentQuestion.id, null);
    } catch (error) {
      console.error('Failed to clear response:', error);
    }
  };

  const navigateToQuestion = (index: number, navigationType: 'NEXT' | 'PREVIOUS' | 'JUMP' | 'SUBJECT_SWITCH' | 'PALETTE_CLICK' = 'JUMP') => {
    if (index >= 0 && index < questions.length) {
      // Track time spent on current question before navigating
      if (questions.length > 0 && currentQuestionIndex >= 0) {
        const currentQuestion = questions[currentQuestionIndex];
        if (currentQuestion && questionStartTime) {
          const timeSpent = Date.now() - questionStartTime;
          setQuestionTimeSpent((prev: Record<string, number>) => ({
            ...prev,
            [currentQuestion.id]: (prev[currentQuestion.id] || 0) + timeSpent
          }));
          setTotalTimeSpent((prev: number) => prev + timeSpent);
        }
      }

      const question = questions[index];
      const fromQuestion = currentQuestionIndex >= 0 ? questions[currentQuestionIndex] : undefined;

      // Track navigation in analytics
      behaviorAnalyticsService.trackQuestionVisit(
        question.id,
        index,
        question.subject,
        question.topic,
        question.difficultyLevel,
        fromQuestion?.id,
        currentQuestionIndex >= 0 ? currentQuestionIndex : undefined,
        navigationType
      );

      setCurrentQuestionIndex(index);

      // Mark question as visited
      setVisitedQuestions((prev: Set<number>) => new Set(prev).add(index));

      // Auto-select the appropriate subject button
      if (question.subject && question.subject !== activeSubject) {
        setActiveSubject(question.subject);
      }

      if (question.questionType === 'NUMERIC_ANSWER' || question.questionType === 'NUMERIC' || question.questionType === 'FILL_IN_THE_BLANK' || question.questionType === 'FILL_IN_THE_BLANK_NUMERIC') {
        setNumericAnswer(String(selectedAnswers && selectedAnswers[question.id] || ''));
      }

      // Set new question start time
      setQuestionStartTime(Date.now());
    }
  };

  // Navigate to first question of a subject
  const navigateToSubject = (subjectName: string) => {
  const firstQuestionIndex = questions.findIndex((q: TestQuestion) => q.subject === subjectName);
    if (firstQuestionIndex !== -1) {
      navigateToQuestion(firstQuestionIndex, 'SUBJECT_SWITCH');
      setActiveSubject(subjectName);
    }
  };





  const handleSaveAndNext = async () => {
    if (isTimeExpired) return; // Block changes after expiry
    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) return;

    // Save current answer if any is selected
    const currentAnswer = selectedAnswers?.[currentQuestion.id];
    if (currentAnswer !== undefined && currentAnswer !== null) {
      try {
        await testService.saveAnswer(currentQuestion.id, currentAnswer);
      } catch (error) {
        console.error('Failed to save answer:', error);
      }
    }

    // Navigate to next question
    if (currentQuestionIndex < questions.length - 1) {
      navigateToQuestion(currentQuestionIndex + 1, 'NEXT');
    }
  };

  const handleSaveAndMarkForReview = async () => {
    if (isTimeExpired) return; // Block changes after expiry
    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) return;

    // Save current answer if any is selected (for "SAVE & MARK FOR REVIEW")
    const currentAnswer = selectedAnswers?.[currentQuestion.id];
    if (currentAnswer !== undefined && currentAnswer !== null) {
      try {
        await testService.saveAnswer(currentQuestion.id, currentAnswer);
      } catch (error) {
        console.error('Failed to save answer:', error);
      }
    }

    // Mark for review
    const newMarked = new Set(markedForReview);
    newMarked.add(currentQuestion.id);
    setMarkedForReview(newMarked);

    // Track review event in analytics
    behaviorAnalyticsService.trackReviewMark(
      currentQuestion.id,
      true,
      currentAnswer !== undefined && currentAnswer !== null
    );

    try {
      await testService.markForReview(currentQuestion.id, true);
    } catch (error) {
      console.error('Failed to mark for review:', error);
    }
  };

  const handleMarkForReviewAndNext = async () => {
    if (isTimeExpired) return; // Block changes after expiry
    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) return;

    // Mark for review WITHOUT saving answer (for "MARK FOR REVIEW & NEXT")
    const newMarked = new Set(markedForReview);
    newMarked.add(currentQuestion.id);
    setMarkedForReview(newMarked);

    try {
      await testService.markForReview(currentQuestion.id, true);
    } catch (error) {
      console.error('Failed to mark for review:', error);
    }

    // Navigate to next question
    if (currentQuestionIndex < questions.length - 1) {
      navigateToQuestion(currentQuestionIndex + 1, 'NEXT');
    }
  };

  const handleManualSubmit = async () => {
    if (isSubmitting) return;

    // Check for unanswered questions
    const unanswered = questions
      .map((question: TestQuestion, index: number) => ({ question, index }))
      .filter(({ question }: { question: TestQuestion }) => !selectedAnswers.hasOwnProperty(question.id))
      .map(({ index }: { index: number }) => index + 1); // Convert to 1-based question numbers

    if (unanswered.length > 0) {
      setUnansweredQuestions(unanswered);
      setShowUnansweredAlert(true);
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to submit your test? You cannot make changes after submission.'
    );
    if (!confirmed) return;

    disableLockdownForSubmission();
    setIsSubmitting(true);
    try {
      // Track time for current question before submitting
      if (questions.length > 0 && currentQuestionIndex >= 0) {
        const currentQuestion = questions[currentQuestionIndex];
        if (currentQuestion && questionStartTime) {
          const timeSpent = Date.now() - questionStartTime;
          setQuestionTimeSpent((prev: Record<string, number>) => ({
            ...prev,
            [currentQuestion.id]: (prev[currentQuestion.id] || 0) + timeSpent
          }));
          setTotalTimeSpent((prev: number) => prev + timeSpent);
        }
      }

      // Get behavior analytics data
      const behaviorAnalytics = behaviorAnalyticsService.getAnalyticsData();

      // Submit test with time tracking data and behavior analytics
      const finalTotalTime = totalTimeSpent + (Date.now() - questionStartTime);
      const result = await testService.submitTest({
        questionTimeSpent,
        totalTimeSpent: finalTotalTime
      }, behaviorAnalytics);

      // Log analytics data for dashboard
      if (testId) {
        try {
          const { auditService } = await import('../lib/auditService');
          await auditService.logTestAnalytics(testId, {
            questionTimeSpent,
            totalTimeSpent: finalTotalTime,
            questionsAnswered: Object.keys(selectedAnswers || {}).length,
            questionsMarked: markedForReview.size
          });
        } catch (error) {
          console.error('Failed to log analytics:', error);
        }
      }

      // Clear localStorage backup data on successful submission
      localStorage.removeItem(`test-answers-${testId}`);
      localStorage.removeItem(`test-marked-${testId}`);

      navigate(`/results/${testId}`, {
        state: { result, autoSubmitted: false }
      });
    } catch (error) {
      console.error('Submit failed:', error);
      setIsSubmitting(false);
      await restoreLockdownAfterFailedSubmission();
    }
  };

  const handleConfirmSubmitWithUnanswered = async () => {
    setShowUnansweredAlert(false);
    disableLockdownForSubmission();
    setIsSubmitting(true);

    try {
      // Track time for current question before submitting
      if (questions.length > 0 && currentQuestionIndex >= 0) {
        const currentQuestion = questions[currentQuestionIndex];
        if (currentQuestion && questionStartTime) {
          const timeSpent = Date.now() - questionStartTime;
          setQuestionTimeSpent((prev: Record<string, number>) => ({
            ...prev,
            [currentQuestion.id]: (prev[currentQuestion.id] || 0) + timeSpent
          }));
          setTotalTimeSpent((prev: number) => prev + timeSpent);
        }
      }

      // Get behavior analytics data
      const behaviorAnalytics = behaviorAnalyticsService.getAnalyticsData();

      // Submit test with time tracking data and behavior analytics
      const finalTotalTime = totalTimeSpent + (Date.now() - questionStartTime);
      const result = await testService.submitTest({
        questionTimeSpent,
        totalTimeSpent: finalTotalTime
      }, behaviorAnalytics);

      // Log analytics data for dashboard
      if (testId) {
        try {
          const { auditService } = await import('../lib/auditService');
          await auditService.logTestAnalytics(testId, {
            questionTimeSpent,
            totalTimeSpent: finalTotalTime,
            questionsAnswered: Object.keys(selectedAnswers || {}).length,
            questionsMarked: markedForReview.size
          });
        } catch (error) {
          console.error('Failed to log analytics:', error);
        }
      }

      // Clear localStorage backup data on successful submission
      localStorage.removeItem(`test-answers-${testId}`);
      localStorage.removeItem(`test-marked-${testId}`);

      navigate(`/results/${testId}`, {
        state: { result, autoSubmitted: false }
      });
    } catch (error) {
      console.error('Submit failed:', error);
      setIsSubmitting(false);
      await restoreLockdownAfterFailedSubmission();
    }
  };



  if (!test || !questions.length || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  // Show loading if questions haven't loaded yet or currentQuestion is undefined
  if (!questions.length || !currentQuestion) {
    return (
      <div className="h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading test questions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 overflow-hidden flex flex-col">

      {/* Orange Header with Subject Tabs and Timer */}
      <div className="text-white shadow-md" style={{ backgroundColor: 'hsl(32.5deg 93.1% 54.51%)' }}>
        <div className="px-4 sm:px-6 py-2">
          <div className="flex items-center justify-between">
            {/* Left: Test Title with Subject Tabs */}
            <div className="flex items-center space-x-4">
              <span className="font-bold text-lg">JEE MAIN</span>

              {/* Subject Tabs next to JEE Main */}
              <div className="flex items-center space-x-1">
                {subjects.map((subject: Subject) => (
                  <button
                    key={subject.id}
                    onClick={() => navigateToSubject(subject.name)}
                    className="px-4 py-2 text-sm font-bold transition-colors text-white"
                    style={{
                      backgroundColor: 'hsl(207.94deg 55.74% 46.08%)',
                      opacity: activeSubject === subject.name ? 1 : 0.8
                    }}
                    title={`Go to first question of ${subject.name}`}
                  >
                    {subject.name.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* Right: Timer and Exit Button */}
            <div className="flex items-center space-x-4">
              <div className="text-right px-4 py-2 rounded">
                <div className="font-bold text-lg">
                  {Math.floor(timeRemaining / 3600)}:{String(Math.floor((timeRemaining % 3600) / 60)).padStart(2, '0')}:{String(timeRemaining % 60).padStart(2, '0')}
                </div>
                <div className="text-xs font-semibold">Time Remaining</div>
              </div>
              <button
                onClick={() => setShowExitDialog(true)}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Exit Test
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Expired Banner */}
      {isTimeExpired && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm font-medium text-center">
          {expiredMessage}
        </div>
      )}

      {/* Main Content Area with Right Sidebar */}
      <div className="flex flex-1 relative bg-white">
        {/* Main Content */}
        <div className="flex-1 overflow-y-auto relative pr-80">
          <div className="p-6">
            <div className="max-w-4xl mx-auto">
              {/* Question Header */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-800">Question {currentQuestionIndex + 1}:</h2>
                </div>
                {/* Thick separator between question label and question text */}
                <hr className="border-gray-400 border-t-2" />
              </div>

              {/* Passage (for Reading Comprehension) */}
              {currentQuestion.passage && (
                <div className="mb-4 mx-4 p-4 bg-amber-50 border border-amber-200 rounded-lg max-h-64 overflow-y-auto">
                  <p className="text-xs font-semibold text-amber-700 uppercase mb-2">Passage</p>
                  <div className="text-gray-800 leading-relaxed text-sm whitespace-pre-wrap" style={{ fontFamily: "'Latin Modern Roman', 'Computer Modern', serif" }}>
                    {renderLatex(currentQuestion.passage)}
                  </div>
                </div>
              )}

              {/* Question Text */}
              <div className="mb-2 text-gray-800 leading-relaxed text-base p-4 font-medium" style={{ fontFamily: "'Latin Modern Roman', 'Computer Modern', serif" }}>
                {renderLatex(getQuestionText(currentQuestion))}
              </div>

              {/* Question Image */}
              {currentQuestion.questionImageUrl && (
                <div className="mb-4 p-4">
                  <img
                    src={currentQuestion.questionImageUrl}
                    alt="Question"
                    className="max-w-full h-auto rounded-lg shadow-sm"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Answer Options */}
              {(currentQuestion.questionType === 'NUMERIC_ANSWER' ||
                currentQuestion.questionType === 'NUMERIC' ||
                currentQuestion.questionType === 'FILL_IN_THE_BLANK' ||
                currentQuestion.questionType === 'FILL_IN_THE_BLANK_NUMERIC') ? (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Your Answer:</label>
                  <div className={isTimeExpired ? 'pointer-events-none opacity-60' : ''}>
                    <VirtualKeyboard
                      value={numericAnswer}
                      onChange={isTimeExpired ? () => {} : handleNumericAnswerChange}
                      onClear={() => (isTimeExpired ? undefined : handleNumericAnswerChange(''))}
                    />
                  </div>

                </div>
              ) : (
                <div className="space-y-3 mb-6">
                  {getOptionsAsObjects(currentQuestion.options).map((option, index) => (
                    <div key={index} className="border border-gray-200 p-3 hover:border-[#0456FF] hover:bg-blue-50 transition-colors">
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <div className="flex items-center space-x-2">
                          <span className="font-bold text-gray-600 text-sm bg-gray-100 w-6 h-6 rounded-full flex items-center justify-center">
                            ({index + 1})
                          </span>
                          {currentQuestion.questionType === 'MCQ_MULTIPLE_CORRECT' ? (
                            <input
                              type="checkbox"
                              name={`question-${currentQuestion.id}`}
                              value={index}
                              checked={Array.isArray(selectedAnswers?.[currentQuestion.id])
                                ? selectedAnswers[currentQuestion.id].includes(index)
                                : selectedAnswers?.[currentQuestion.id] === index}
                              onChange={() => handleMultipleAnswerSelect(currentQuestion.id, index)}
                              className="w-4 h-4 text-[#0456FF] focus:ring-[#0456FF] focus:ring-2"
                              disabled={isTimeExpired}
                            />
                          ) : (
                            <input
                              type="radio"
                              name={`question-${currentQuestion.id}`}
                              value={index}
                              checked={selectedAnswers && selectedAnswers[currentQuestion.id] === index}
                              onChange={() => handleAnswerSelect(currentQuestion.id, index)}
                              className="w-4 h-4 text-[#0456FF] focus:ring-[#0456FF] focus:ring-2"
                              disabled={isTimeExpired}
                            />
                          )}
                        </div>
                        <div className="flex-1">
                          <span className="text-gray-800 text-sm leading-relaxed font-medium" style={{ fontFamily: "'Latin Modern Roman', 'Computer Modern', serif" }}>
                            {renderLatex(option.text)}
                          </span>
                          {/* Option Image */}
                          {option.imageUrl && (
                            <div className="mt-2">
                              <img
                                src={option.imageUrl}
                                alt={`Option ${index + 1}`}
                                className="max-w-full h-auto rounded-lg shadow-sm max-h-48"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              )}

              {/* Separator between options and action buttons */}
              <hr className="border-gray-300 mb-6" />

              {/* Action Buttons */}
              <div className="mt-8">
                <div className="flex flex-wrap gap-3 mb-6">
                  <button
                    className="px-6 py-2 text-sm font-bold transition-colors text-white"
                    style={{ backgroundColor: 'hsl(120.65deg 39.32% 54.12%)' }}
                    onClick={handleSaveAndNext}
                    disabled={isTimeExpired}
                  >
                    SAVE & NEXT
                  </button>
                  <button
                    className="px-6 py-2 text-sm font-bold transition-colors text-white"
                    style={{ backgroundColor: 'hsl(34.76deg 86.32% 62.75%)' }}
                    onClick={handleSaveAndMarkForReview}
                    disabled={isTimeExpired}
                  >
                    SAVE & MARK FOR REVIEW
                  </button>
                  <button
                    className="px-6 py-2 text-sm font-bold transition-colors text-black border border-gray-300 hover:bg-gray-50"
                    style={{ backgroundColor: 'hsl(0deg 0% 100%)' }}
                    onClick={handleClearResponse}
                    disabled={isTimeExpired}
                  >
                    CLEAR RESPONSE
                  </button>
                  <button
                    className="px-6 py-2 text-sm font-bold transition-colors text-white"
                    style={{ backgroundColor: 'hsl(207.94deg 55.74% 46.08%)' }}
                    onClick={handleMarkForReviewAndNext}
                    disabled={isTimeExpired}
                  >
                    MARK FOR REVIEW & NEXT
                  </button>
                </div>

                {/* Separator between save buttons and navigation buttons */}
                <hr className="border-gray-300 mb-6" />

                {/* Navigation and Submit Buttons in single line */}
                <div className="flex items-center justify-between">
                  {/* Left: Back and Next buttons together */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => navigateToQuestion(currentQuestionIndex - 1)}
                      disabled={currentQuestionIndex === 0}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Previous Question"
                    >
                      &lt;&lt; BACK
                    </button>

                    <button
                      onClick={() => navigateToQuestion(currentQuestionIndex + 1)}
                      disabled={currentQuestionIndex === questions.length - 1}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Next Question"
                    >
                      NEXT &gt;&gt;
                    </button>
                  </div>

                  {/* Right: Submit button */}
                  <button
                    className="text-white px-8 py-2 text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: 'hsl(120.65deg 39.32% 54.12%)' }}
                    onClick={handleManualSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'SUBMIT'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>



        {/* Floating Navigation Controls */}
        <div className="floating-nav-controls floating-nav-left">
          <button
            onClick={() => navigateToQuestion(currentQuestionIndex - 1)}
            disabled={currentQuestionIndex === 0}
            className="floating-nav-button"
            title="Previous Question"
          >
            &lt;
          </button>
        </div>

        <div className="floating-nav-controls floating-nav-right">
          <button
            onClick={() => navigateToQuestion(currentQuestionIndex + 1)}
            disabled={currentQuestionIndex === questions.length - 1}
            className="floating-nav-button"
            title="Next Question"
          >
            &gt;
          </button>
        </div>

        {/* Question Navigation Panel - Fixed Right Sidebar */}
        <div className="fixed top-0 right-0 w-80 h-full bg-white border-l border-gray-200 z-30 overflow-y-auto"
             style={{ top: '120px' }}>
          <div className="p-4">
            {/* Legend */}
            <div className="mb-6">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-gray-400 text-white text-xs flex items-center justify-center font-bold rounded">
                    {questions.filter((_, index) => {
                      const status = getQuestionStatus(questions[index], index);
                      return status === 'not-visited';
                    }).length}
                  </div>
                  <span className="font-bold text-gray-700">Not Visited</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="round-badge text-xs flex items-center justify-center font-bold" style={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: 'hsl(17.71deg 99.13% 50%)',
                    color: 'white'
                  }}>
                    {questions.filter((_, index) => {
                      const status = getQuestionStatus(questions[index], index);
                      return status === 'unanswered';
                    }).length}
                  </div>
                  <span className="font-bold text-gray-700">Not Answered</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="round-badge text-xs flex items-center justify-center font-bold" style={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: 'hsl(104.83deg 96.67% 35.29%)',
                    color: 'white'
                  }}>
                    {questions.filter((_, index) => {
                      const status = getQuestionStatus(questions[index], index);
                      return status === 'answered';
                    }).length}
                  </div>
                  <span className="font-bold text-gray-700">Answered</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="round-badge round-badge-marked text-xs flex items-center justify-center font-bold">
                    {questions.filter((_, index) => {
                      const status = getQuestionStatus(questions[index], index);
                      return status === 'marked';
                    }).length}
                  </div>
                  <span className="font-bold text-gray-700">Marked for Review</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="round-badge round-badge-answered-marked text-xs flex items-center justify-center font-bold">
                    {questions.filter((_, index) => {
                      const status = getQuestionStatus(questions[index], index);
                      return status === 'answered-marked';
                    }).length}
                  </div>
                  <span className="font-bold text-gray-700">Answered & Marked</span>
                </div>
              </div>
            </div>

            {/* Question Grid */}
            <div className="grid grid-cols-8 gap-2">
              {questions.map((question, index) => {
                const status = getQuestionStatus(question, index);

                if (status === 'current') {
                  return (
                    <button
                      key={question.id}
                      onClick={() => navigateToQuestion(index)}
                      style={{ backgroundColor: 'hsl(32.5deg 93.1% 54.51%)' }}
                      className="w-8 h-8 text-white text-xs font-bold hover:scale-105 transition-all duration-200 rounded"
                    >
                      {String(index + 1).padStart(2, '0')}
                    </button>
                  );
                } else if (status === 'answered') {
                  return (
                    <button
                      key={question.id}
                      onClick={() => navigateToQuestion(index)}
                      className="round-badge text-xs font-bold hover:scale-105 transition-all duration-200"
                      style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: 'hsl(104.83deg 96.67% 35.29%)', // Green for answered
                        color: 'white'
                      }}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </button>
                  );
                } else if (status === 'unanswered') {
                  return (
                    <button
                      key={question.id}
                      onClick={() => navigateToQuestion(index)}
                      className="round-badge text-xs font-bold hover:scale-105 transition-all duration-200"
                      style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: 'hsl(17.71deg 99.13% 50%)', // Red for unanswered
                        color: 'white'
                      }}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </button>
                  );
                } else if (status === 'marked') {
                  return (
                    <button
                      key={question.id}
                      onClick={() => navigateToQuestion(index)}
                      className="round-badge round-badge-marked text-xs font-bold hover:scale-105 transition-all duration-200"
                      style={{ width: '32px', height: '32px' }}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </button>
                  );
                } else if (status === 'answered-marked') {
                  return (
                    <button
                      key={question.id}
                      onClick={() => navigateToQuestion(index)}
                      className="round-badge round-badge-answered-marked text-xs font-bold hover:scale-105 transition-all duration-200"
                      style={{ width: '32px', height: '32px' }}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </button>
                  );
                } else {
                  // not-visited
                  return (
                    <button
                      key={question.id}
                      onClick={() => navigateToQuestion(index)}
                      className="w-8 h-8 text-white text-xs font-bold bg-gray-400 hover:scale-105 transition-all duration-200 rounded"
                    >
                      {String(index + 1).padStart(2, '0')}
                    </button>
                  );
                }
              })}
            </div>
          </div>
        </div>
      </div>
      {/* Time Up Modal */}
      {showTimeUpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg max-w-md mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-red-600 mb-2">Time's Up!</h3>
              <p className="text-gray-600 mb-4">
                Your test time has expired. Your answers have been automatically submitted.
              </p>
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Redirecting to results...</p>
            </div>
          </div>
        </div>
      )}

      {/* Submit Confirmation Modal */}
      {isSubmitting && !showTimeUpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold mb-2">Submitting Test...</h3>
              <p className="text-gray-600">
                Please wait while we process your submission.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Unanswered Questions Alert Dialog */}
      <AlertDialog open={showUnansweredAlert} onOpenChange={setShowUnansweredAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className={`flex items-center gap-2 ${unansweredQuestions.length > 10 ? 'text-red-600' : 'text-orange-600'}`}>
              <AlertTriangle className="w-5 h-5" aria-hidden="true" />
              {unansweredQuestions.length > 10 ? "Many Unanswered Questions" : "Unanswered Questions"}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              {unansweredQuestions.length > 10 ? (
                <>
                  <span className="block mb-2">
                    You have <strong>{unansweredQuestions.length} unanswered questions</strong> in your test.
                  </span>
                  <span className="block text-sm text-gray-600">
                    Are you sure you want to submit your test with these unanswered questions?
                    You cannot make changes after submission.
                  </span>
                </>
              ) : (
                <>
                  <span className="block mb-2">
                    You have unanswered questions: <strong>Question {unansweredQuestions.join(', ')}</strong>
                  </span>
                  <span className="block text-sm text-gray-600">
                    Are you sure you want to submit your test with these unanswered questions?
                    You cannot make changes after submission.
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Review Questions</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmSubmitWithUnanswered}
              className="bg-red-600 hover:bg-red-700"
            >
              Submit Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Exit Test Confirmation Dialog */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2"><AlertTriangle className="w-5 h-5" aria-hidden="true" />Exit Test Confirmation</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <span className="block font-semibold text-gray-800">
                Are you sure you want to exit this test?
              </span>
              <span className="block bg-yellow-50 border border-yellow-200 rounded p-3">
                <span className="block text-yellow-700 font-medium">
                  <strong>NOTE:</strong> If you exit now:
                </span>
                <span className="block text-yellow-600 text-sm mt-2">
                  • Your progress will be saved<br/>
                  • You can resume this test later<br/>
                  • The timer will continue running<br/>
                  • You must return before time expires
                </span>
              </span>
              <span className="block text-sm text-gray-600">
                Use "Submit Test" button when you're finished with all questions.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Stay in Test</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowExitDialog(false);
                // Just navigate away - progress is already saved via periodic sync
                // Clear local storage but don't submit
                localStorage.removeItem(`test-answers-${testId}`);
                localStorage.removeItem(`test-marked-${testId}`);
                navigate('/');
              }}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              Exit & Save Progress
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Exam Lockdown Warning Banner */}
      {lockdownState.warningMessage && (
        <div className="fixed top-0 left-0 right-0 z-[60] flex items-center justify-center bg-red-600 text-white px-4 py-3 shadow-lg animate-pulse">
          <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm font-semibold">{lockdownState.warningMessage}</span>
        </div>
      )}
    </div>
  );
};

export default EnhancedTestTakingPage;
